import React, { useEffect, useMemo, useState } from 'react';
import { Download, LogOut, MailWarning, ShieldCheck, Users, Volleyball } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { apiClient } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { AdminTableSection } from '../components/admin/AdminTableSection';

const archiveOptions = ['active', 'archived', 'all'];

const createCsv = (rows, columns) => {
  const headers = columns.map((column) => column.label).join(',');
  const body = rows.map((row) => columns.map((column) => {
    const rawValue = column.exportValue ? column.exportValue(row) : row[column.key];
    const safeValue = String(rawValue ?? '').replace(/"/g, '""');
    return `"${safeValue}"`;
  }).join(',')).join('\n');
  return `${headers}\n${body}`;
};

const downloadCsv = (filename, rows, columns) => {
  const blob = new Blob([createCsv(rows, columns)], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const matchesArchiveFilter = (row, value) => value === 'all' || (value === 'archived' ? row.archived : !row.archived);

const badge = (text, background, color, testId) => <Badge style={{ backgroundColor: background, color }} data-testid={testId}>{text}</Badge>;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [records, setRecords] = useState({ tryouts: [], contacts: [], members: [], support: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    tryoutsSearch: '',
    tryoutsArchive: 'active',
    contactsSearch: '',
    contactsArchive: 'active',
    contactsStatus: 'all',
    membersSearch: '',
    membersArchive: 'active',
    supportSearch: '',
    supportArchive: 'active',
  });

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [tryouts, contacts, members, support] = await Promise.all([
        apiClient.get('/admin/tryout-registrations'),
        apiClient.get('/admin/contacts'),
        apiClient.get('/admin/members'),
        apiClient.get('/admin/support-inquiries'),
      ]);
      setRecords({
        tryouts: tryouts.data,
        contacts: contacts.data,
        members: members.data,
        support: support.data,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const stats = useMemo(() => ({
    totalTryouts: records.tryouts.filter((row) => !row.archived).length,
    unreadContacts: records.contacts.filter((row) => row.status === 'unread' && !row.archived).length,
    memberApplications: records.members.filter((row) => !row.archived).length,
    supportInquiries: records.support.filter((row) => !row.archived).length,
  }), [records]);

  const filteredTryouts = useMemo(() => records.tryouts.filter((row) => {
    const query = `${row.playerName} ${row.parentName} ${row.currentLeagueLevel} ${row.preferredPosition}`.toLowerCase();
    return query.includes(filters.tryoutsSearch.toLowerCase()) && matchesArchiveFilter(row, filters.tryoutsArchive);
  }), [records.tryouts, filters.tryoutsSearch, filters.tryoutsArchive]);

  const filteredContacts = useMemo(() => records.contacts.filter((row) => {
    const query = `${row.name} ${row.email} ${row.subject} ${row.message}`.toLowerCase();
    const statusMatch = filters.contactsStatus === 'all' || row.status === filters.contactsStatus;
    return query.includes(filters.contactsSearch.toLowerCase()) && statusMatch && matchesArchiveFilter(row, filters.contactsArchive);
  }), [records.contacts, filters.contactsSearch, filters.contactsArchive, filters.contactsStatus]);

  const filteredMembers = useMemo(() => records.members.filter((row) => {
    const query = `${row.firstName} ${row.lastName} ${row.email} ${row.tribe} ${row.school}`.toLowerCase();
    return query.includes(filters.membersSearch.toLowerCase()) && matchesArchiveFilter(row, filters.membersArchive);
  }), [records.members, filters.membersSearch, filters.membersArchive]);

  const filteredSupport = useMemo(() => records.support.filter((row) => {
    const query = `${row.name} ${row.email} ${row.message} ${row.inquiryType}`.toLowerCase();
    return query.includes(filters.supportSearch.toLowerCase()) && matchesArchiveFilter(row, filters.supportArchive);
  }), [records.support, filters.supportSearch, filters.supportArchive]);

  const patchRecord = async (route, id, payload) => {
    await apiClient.patch(`${route}/${id}`, payload);
    await loadDashboard();
  };

  const tryoutColumns = [
    { key: 'playerName', label: 'Player' },
    { key: 'playerAge', label: 'Age' },
    { key: 'currentLeagueLevel', label: 'League' },
    { key: 'preferredPosition', label: 'Preferred Position' },
    { key: 'otherPositions', label: 'Other Positions', render: (row) => row.otherPositions.join(', ') || '—', exportValue: (row) => row.otherPositions.join('; ') },
    { key: 'battingSkills', label: 'Batting Skills', render: (row) => row.battingSkills.join(', ') || '—', exportValue: (row) => row.battingSkills.join('; ') },
    { key: 'currentTeam', label: 'Current Team' },
    { key: 'parentName', label: 'Parent / Guardian' },
    { key: 'playerContactNumber', label: 'Player Contact' },
  ];

  const contactColumns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'subject', label: 'Subject' },
    { key: 'message', label: 'Message' },
    { key: 'status', label: 'Status', render: (row) => badge(row.status, row.status === 'unread' ? '#dbbf57' : '#53a5ca', '#fff', `contact-status-${row.id}`) },
  ];

  const memberColumns = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'school', label: 'School' },
    { key: 'tribe', label: 'Tribe / Nation' },
    { key: 'status', label: 'Status', render: (row) => badge(row.status, '#53a5ca', '#fff', `member-status-${row.id}`) },
  ];

  const supportColumns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'inquiryType', label: 'Inquiry Type' },
    { key: 'message', label: 'Message' },
    { key: 'status', label: 'Status', render: (row) => badge(row.status, '#e37c55', '#fff', `support-status-${row.id}`) },
  ];

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-8" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto space-y-8">
        <section className="rounded-[32px] overflow-hidden shadow-2xl" style={{ background: 'linear-gradient(135deg, #7e2f22 0%, #53a5ca 100%)' }}>
          <div className="px-8 py-10 text-white flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold" data-testid="dashboard-access-badge">
                <ShieldCheck className="h-4 w-4" /> Protected operations dashboard
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold">NAIWA Daughters admin dashboard</h1>
              <p className="max-w-2xl text-white/90">Review incoming registrations and messages, keep responses organized, archive completed items, and export records to spreadsheets.</p>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="rounded-2xl bg-white/15 px-4 py-3" data-testid="dashboard-user-card">
                <p className="text-xs uppercase tracking-wide text-white/70">Signed in as</p>
                <p className="font-semibold">{user?.email}</p>
              </div>
              <Button onClick={logout} variant="outline" className="border-white text-white bg-transparent hover:bg-white hover:text-[#7e2f22]" data-testid="dashboard-logout-button">
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </Button>
            </div>
          </div>
        </section>

        <section className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { label: 'Active tryout registrations', value: stats.totalTryouts, icon: Volleyball },
            { label: 'Unread contact messages', value: stats.unreadContacts, icon: MailWarning },
            { label: 'Membership applications', value: stats.memberApplications, icon: Users },
            { label: 'Support inquiries', value: stats.supportInquiries, icon: Download },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="border-0 shadow-lg" data-testid={`dashboard-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <CardContent className="pt-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                    <p className="text-4xl font-bold" style={{ color: '#7e2f22' }}>{stat.value}</p>
                  </div>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#53a5ca20' }}>
                    <Icon className="h-7 w-7" style={{ color: '#53a5ca' }} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <Tabs defaultValue="tryouts" className="space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0" data-testid="dashboard-tabs-list">
            <TabsTrigger value="tryouts" data-testid="dashboard-tab-tryouts">Tryout Registrations</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="dashboard-tab-contacts">Contact Messages</TabsTrigger>
            <TabsTrigger value="members" data-testid="dashboard-tab-members">Membership Applications</TabsTrigger>
            <TabsTrigger value="support" data-testid="dashboard-tab-support">Support Inquiries</TabsTrigger>
          </TabsList>

          <TabsContent value="tryouts">
            <AdminTableSection
              title="Tryout registrations"
              description="View pre-registrations, position data, and batting skills for upcoming Softball for Sovereignty tryouts."
              rows={filteredTryouts}
              columns={tryoutColumns}
              searchValue={filters.tryoutsSearch}
              onSearchChange={(value) => setFilters((current) => ({ ...current, tryoutsSearch: value }))}
              searchPlaceholder="Search player, parent, league, or position"
              toolbar={<select className="h-10 rounded-md border px-3" value={filters.tryoutsArchive} onChange={(event) => setFilters((current) => ({ ...current, tryoutsArchive: event.target.value }))} data-testid="tryouts-archive-filter">{archiveOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select>}
              onExport={() => downloadCsv('naiwa-tryout-registrations.csv', filteredTryouts, tryoutColumns)}
              exportLabel="Export CSV"
              emptyText={loading ? 'Loading registrations…' : 'No tryout registrations match these filters yet.'}
              actions={[
                {
                  label: (row) => row.archived ? 'Restore' : 'Archive',
                  onClick: (row) => patchRecord('/admin/tryout-registrations', row.id, { archived: !row.archived }),
                  testId: (row) => `tryout-archive-toggle-${row.id}`,
                },
              ]}
              testId="tryouts"
            />
          </TabsContent>

          <TabsContent value="contacts">
            <AdminTableSection
              title="Contact messages"
              description="Review messages from the contact form, mark them read or unread, and archive completed conversations."
              rows={filteredContacts}
              columns={contactColumns}
              searchValue={filters.contactsSearch}
              onSearchChange={(value) => setFilters((current) => ({ ...current, contactsSearch: value }))}
              searchPlaceholder="Search name, email, subject, or message"
              toolbar={[
                <select key="archive" className="h-10 rounded-md border px-3" value={filters.contactsArchive} onChange={(event) => setFilters((current) => ({ ...current, contactsArchive: event.target.value }))} data-testid="contacts-archive-filter">{archiveOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select>,
                <select key="status" className="h-10 rounded-md border px-3" value={filters.contactsStatus} onChange={(event) => setFilters((current) => ({ ...current, contactsStatus: event.target.value }))} data-testid="contacts-status-filter">{['all', 'unread', 'read'].map((option) => <option key={option} value={option}>{option}</option>)}</select>,
              ]}
              onExport={() => downloadCsv('naiwa-contact-messages.csv', filteredContacts, contactColumns)}
              exportLabel="Export CSV"
              emptyText={loading ? 'Loading contact messages…' : 'No contact messages match these filters yet.'}
              actions={[
                {
                  label: (row) => row.status === 'unread' ? 'Mark read' : 'Mark unread',
                  onClick: (row) => patchRecord('/admin/contacts', row.id, { status: row.status === 'unread' ? 'read' : 'unread' }),
                  testId: (row) => `contact-status-toggle-${row.id}`,
                },
                {
                  label: (row) => row.archived ? 'Restore' : 'Archive',
                  onClick: (row) => patchRecord('/admin/contacts', row.id, { archived: !row.archived }),
                  testId: (row) => `contact-archive-toggle-${row.id}`,
                },
              ]}
              testId="contacts"
            />
          </TabsContent>

          <TabsContent value="members">
            <AdminTableSection
              title="Membership applications"
              description="Track incoming join applications and archive entries after follow-up is complete."
              rows={filteredMembers}
              columns={memberColumns}
              searchValue={filters.membersSearch}
              onSearchChange={(value) => setFilters((current) => ({ ...current, membersSearch: value }))}
              searchPlaceholder="Search applicant, tribe, email, or school"
              toolbar={<select className="h-10 rounded-md border px-3" value={filters.membersArchive} onChange={(event) => setFilters((current) => ({ ...current, membersArchive: event.target.value }))} data-testid="members-archive-filter">{archiveOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select>}
              onExport={() => downloadCsv('naiwa-membership-applications.csv', filteredMembers, memberColumns)}
              exportLabel="Export CSV"
              emptyText={loading ? 'Loading member applications…' : 'No membership applications match these filters yet.'}
              actions={[
                {
                  label: (row) => row.archived ? 'Restore' : 'Archive',
                  onClick: (row) => patchRecord('/admin/members', row.id, { archived: !row.archived }),
                  testId: (row) => `member-archive-toggle-${row.id}`,
                },
              ]}
              testId="members"
            />
          </TabsContent>

          <TabsContent value="support">
            <AdminTableSection
              title="Support inquiries"
              description="Keep track of grant and partnership conversations coming through the support page."
              rows={filteredSupport}
              columns={supportColumns}
              searchValue={filters.supportSearch}
              onSearchChange={(value) => setFilters((current) => ({ ...current, supportSearch: value }))}
              searchPlaceholder="Search name, email, message, or inquiry type"
              toolbar={<select className="h-10 rounded-md border px-3" value={filters.supportArchive} onChange={(event) => setFilters((current) => ({ ...current, supportArchive: event.target.value }))} data-testid="support-archive-filter">{archiveOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select>}
              onExport={() => downloadCsv('naiwa-support-inquiries.csv', filteredSupport, supportColumns)}
              exportLabel="Export CSV"
              emptyText={loading ? 'Loading support inquiries…' : 'No support inquiries match these filters yet.'}
              actions={[
                {
                  label: (row) => row.archived ? 'Restore' : 'Archive',
                  onClick: (row) => patchRecord('/admin/support-inquiries', row.id, { archived: !row.archived }),
                  testId: (row) => `support-archive-toggle-${row.id}`,
                },
              ]}
              testId="support"
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}